import { useState, lazy, Suspense } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { Person, Order } from '@/types';
import { OrderForm } from '@/components/Forms/OrderForm';
import { RushOrderForm } from '@/components/Forms/RushOrderForm';
import { Footer } from '@/components/Footer';

// 延遲載入甘特圖組件以減少初始 bundle 大小
const WorkOrderGantt = lazy(() => import('@/components/GanttChart/WorkOrderGantt').then(m => ({ default: m.WorkOrderGantt })));

type TabType = 'overview' | 'orders' | 'persons' | 'gantt';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showRushOrderForm, setShowRushOrderForm] = useState(false);

  const orders = useOrderStore((state) => state.orders);
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const persons = useResourceStore((state) => state.persons);
  const machines = useResourceStore((state) => state.machines);
  const materials = useResourceStore((state) => state.materials);

  // 計算統計數據
  const stats = {
    totalOrders: orders.length,
    inProgressOrders: orders.filter((o) => o.status === '進行中').length,
    totalWorkOrders: mainWorkOrders.length,
    totalSubWorkOrders: subWorkOrders.length,
    totalPersons: persons.length,
    totalMachines: machines.length,
    lowStockMaterials: materials.filter((m) => m.stockQuantity <= m.safetyStock).length,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">製造排程系統 - POC Demo</h1>
              <p className="text-sm text-gray-500 mt-1">訂單驅動的製造流程管理系統</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                靜態部署模式
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {(['overview', 'orders', 'persons', 'gantt'] as TabType[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'overview' && '總覽'}
                {tab === 'orders' && '訂單管理'}
                {tab === 'persons' && '人員資源'}
                {tab === 'gantt' && '甘特圖'}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard label="總訂單數" value={stats.totalOrders} color="bg-blue-500" />
              <StatCard label="進行中訂單" value={stats.inProgressOrders} color="bg-yellow-500" />
              <StatCard label="主工單數" value={stats.totalWorkOrders} color="bg-green-500" />
              <StatCard label="子工單數" value={stats.totalSubWorkOrders} color="bg-purple-500" />
              <StatCard label="人員數" value={stats.totalPersons} color="bg-indigo-500" />
              <StatCard label="機台數" value={stats.totalMachines} color="bg-pink-500" />
              <StatCard label="低庫存物料" value={stats.lowStockMaterials} color="bg-red-500" />
            </div>

            {/* System Info */}
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">系統資訊</h2>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• 部署方式：Cloudflare Pages（靜態頁面託管）</p>
                <p>• 資料來源：reference-data/*.json</p>
                <p>• 技術棧：React + TypeScript + Tailwind CSS + Zustand</p>
                <p>• 甘特圖：DHTMLX Gantt（待整合）</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">訂單列表</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowOrderForm(true)}
                  className="btn-primary"
                >
                  新增訂單
                </button>
                <button
                  onClick={() => setShowRushOrderForm(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  🚨 插單
                </button>
              </div>
            </div>
            <OrderList orders={orders} />
          </div>
        )}

        {activeTab === 'persons' && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">人員列表</h2>
            <PersonList persons={persons} />
          </div>
        )}

        {activeTab === 'gantt' && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">工單甘特圖</h2>
            <Suspense fallback={<div className="text-center py-12 text-gray-500">載入甘特圖中...</div>}>
              <WorkOrderGantt />
            </Suspense>
          </div>
        )}
      </main>

      {/* 表單對話框 */}
      {showOrderForm && <OrderForm onClose={() => setShowOrderForm(false)} />}
      {showRushOrderForm && <RushOrderForm onClose={() => setShowRushOrderForm(false)} />}

      {/* Footer 版本資訊 */}
      <Footer />
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`h-12 w-12 rounded-lg ${color} opacity-20`}></div>
      </div>
    </div>
  );
}

// Order List Component
function OrderList({ orders }: { orders: Order[] }) {
  if (orders.length === 0) {
    return <p className="text-gray-500 text-center py-8">暫無訂單資料</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              訂單編號
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              產品名稱
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              客戶
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              數量
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              預計交期
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              進度
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              狀態
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orders.map((order) => (
            <tr key={order.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {order.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.productName}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.customer}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.totalQuantity}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(order.plannedDeliveryDate).toLocaleDateString('zh-TW')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="flex items-center">
                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full"
                      style={{ width: `${order.progress}%` }}
                    ></div>
                  </div>
                  <span className="text-xs">{order.progress}%</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    order.status === '已完成'
                      ? 'bg-green-100 text-green-800'
                      : order.status === '進行中'
                      ? 'bg-yellow-100 text-yellow-800'
                      : order.status === '已延遲'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {order.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Person List Component
function PersonList({ persons }: { persons: Person[] }) {
  if (persons.length === 0) {
    return <p className="text-gray-500 text-center py-8">暫無人員資料</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              工號
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              姓名
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              部門
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              廠別
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              角色
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              班表
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              狀態
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {persons.map((person) => (
            <tr key={person.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {person.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.name}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.department}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {person.plants.join(', ')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {person.roles.join(', ')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{person.shiftType}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    person.status === '啟用'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {person.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
